import { useEffect, useEffectEvent, useRef, useState } from 'react';
import { AnimatePresence, motion } from 'motion/react';
import {
    StanAccordion,
    StanAccordionBody,
    StanAccordionHeader,
    StanAccordionItem,
    StanDataRowGroup,
} from '@/components';
import { useAccordionController } from '@/hooks/useAccordionController';
import type { FunnelListItem } from '../../types';
import { FunnelRow } from './FunnelRow';
import { FunnelRowSkeleton } from './FunnelRowSkeleton';
import { FunnelPagesExpanded } from './expanded/FunnelPagesExpanded';
import { FUNNELS_LIST_HEADER_COLUMNS } from '../../constants/funnelsListColumns';

interface FunnelsListProps {
    funnels: FunnelListItem[];
    onEdit: (funnel: FunnelListItem) => void;
    onDuplicate: (funnel: FunnelListItem) => void;
    onDelete: (funnel: FunnelListItem) => void;
    isDuplicating?: boolean;
}

/**
 * Funnels list rendered as accordions — each row can expand to reveal
 * the funnel's pages. Mirrors the FlowsTab pattern in email-flows.
 */
export const FunnelsList = ({ funnels, onEdit, onDuplicate, onDelete, isDuplicating = false }: FunnelsListProps) => {
    const [newlyDuplicatedId, setNewlyDuplicatedId] = useState<number | null>(null);
    const prevCountRef = useRef(funnels.length);
    const accordionController = useAccordionController({ allowMultipleOpen: false });

    const handleNewFunnelDetection = useEffectEvent((funnelList: FunnelListItem[]) => {
        const currentCount = funnelList.length;
        const prevCount = prevCountRef.current;

        if (currentCount > prevCount) {
            // Newest funnel is first (sorted by store_id desc)
            const newFunnel = funnelList[0];
            if (newFunnel) {
                setNewlyDuplicatedId(newFunnel.store_id);
                // Auto-expand the freshly-duplicated funnel so the user sees its pages.
                accordionController.openItem(`funnel-${newFunnel.store_id}`);
                setTimeout(() => {
                    setNewlyDuplicatedId(null);
                }, 500);
            }
        }

        prevCountRef.current = currentCount;
    });

    useEffect(() => {
        handleNewFunnelDetection(funnels);
    }, [funnels]);

    // Collapse the source funnel before firing the parent's duplicate handler —
    // it's about to get pushed down by the skeleton + the new row, and leaving it
    // open makes the list jumpy.
    const handleDuplicate = (funnel: FunnelListItem) => {
        accordionController.closeItem(`funnel-${funnel.store_id}`);
        onDuplicate(funnel);
    };

    return (
        <StanDataRowGroup headerColumns={FUNNELS_LIST_HEADER_COLUMNS} maxActionButtonCount={1}>
            <StanAccordion controller={accordionController}>
                <AnimatePresence initial={false}>
                    {isDuplicating && (
                        <motion.div
                            key='funnel-duplicating-skeleton'
                            initial={{ opacity: 0, height: 0, marginBottom: 0 }}
                            animate={{ opacity: 1, height: 'auto', marginBottom: 24 }}
                            exit={{ opacity: 0, height: 0, marginBottom: 0 }}
                            transition={{ duration: 0.3, ease: 'backOut' }}
                        >
                            <FunnelRowSkeleton />
                        </motion.div>
                    )}
                    {funnels.map((funnel, index) => {
                        const isFirst = index === 0 && !isDuplicating;

                        return (
                            <motion.div
                                key={funnel.store_id}
                                initial={{ opacity: 0, height: 0, marginTop: 0 }}
                                animate={{
                                    opacity: 1,
                                    height: 'auto',
                                    marginTop: isFirst ? 0 : 24,
                                }}
                                exit={{ opacity: 0, height: 0, marginTop: 0 }}
                                transition={{ duration: 0.4, ease: 'backOut' }}
                            >
                                <StanAccordionItem
                                    id={`funnel-${funnel.store_id}`}
                                    className='bg-canvas shadow-stan! overflow-hidden rounded-lg'
                                >
                                    <StanAccordionHeader asChild className=''>
                                        <FunnelRow
                                            funnel={funnel}
                                            onEdit={onEdit}
                                            onDuplicate={handleDuplicate}
                                            onDelete={onDelete}
                                            isNewlyDuplicated={newlyDuplicatedId === funnel.store_id}
                                            expanded={accordionController.isItemOpen(`funnel-${funnel.store_id}`)}
                                        />
                                    </StanAccordionHeader>
                                    <StanAccordionBody className='relative'>
                                        <hr className='absolute -mt-12 w-full -translate-x-4 -translate-y-8' />
                                        <FunnelPagesExpanded storeId={funnel.store_id} pages={funnel.pages} />
                                    </StanAccordionBody>
                                </StanAccordionItem>
                            </motion.div>
                        );
                    })}
                </AnimatePresence>
            </StanAccordion>
        </StanDataRowGroup>
    );
};
